const AUTH_CONSISTENCY_ACCOUNT_AGE_WINDOW_MS = 30_000
export const DEFAULT_AUTH_CONSISTENCY_BOOTSTRAP_WINDOW_MS = 10_000

export const AUTH_CONSISTENCY_HEADER_NAME = 'X-Auth-Consistency'
export const AUTH_CONSISTENCY_FULLY_CONSISTENT_VALUE = 'fully_consistent'

type AccountCreatedAt = Date | number | string | null | undefined

const normalizeTimestampMs = (value: AccountCreatedAt): number | null => {
  if (value == null) {
    return null
  }

  let timestampMs: number
  if (value instanceof Date) {
    timestampMs = value.getTime()
  } else if (typeof value === 'number') {
    timestampMs = value
  } else {
    const numericValue = Number(value)
    timestampMs =
      Number.isFinite(numericValue) && numericValue > 0
        ? numericValue
        : Date.parse(String(value))
  }

  if (!Number.isFinite(timestampMs) || timestampMs <= 0) {
    return null
  }

  return timestampMs < 1_000_000_000_000 ? timestampMs * 1_000 : timestampMs
}

const getAuthConsistencyDeadlineMs = (): number | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const deadlineMs = window.__AUTH_CONSISTENCY_FULLY_CONSISTENT_UNTIL_MS__
  if (!deadlineMs || !Number.isFinite(deadlineMs)) {
    delete window.__AUTH_CONSISTENCY_FULLY_CONSISTENT_UNTIL_MS__
    return null
  }

  return deadlineMs
}

const setAuthConsistencyDeadlineMs = (deadlineMs: number | null): void => {
  if (typeof window === 'undefined') {
    return
  }

  if (deadlineMs === null || !Number.isFinite(deadlineMs)) {
    return
  }

  const currentDeadlineMs = getAuthConsistencyDeadlineMs()
  if (currentDeadlineMs !== null && currentDeadlineMs >= deadlineMs) {
    return
  }

  window.__AUTH_CONSISTENCY_FULLY_CONSISTENT_UNTIL_MS__ = deadlineMs
}

export const setAuthConsistencyAccountCreatedAt = (
  accountCreatedAt: AccountCreatedAt,
) => {
  if (typeof window === 'undefined') {
    return
  }

  const createdAtMs = normalizeTimestampMs(accountCreatedAt)
  if (createdAtMs === null) {
    return
  }

  setAuthConsistencyDeadlineMs(
    createdAtMs + AUTH_CONSISTENCY_ACCOUNT_AGE_WINDOW_MS,
  )
}

export const clearAuthConsistencyAccountCreatedAt = () => {
  if (typeof window === 'undefined') {
    return
  }

  delete window.__AUTH_CONSISTENCY_FULLY_CONSISTENT_UNTIL_MS__
  delete window.__AUTH_CONSISTENCY_LAST_BOOTSTRAP_KEY__
}

export const extendAuthConsistencyWindow = (windowMs: number) => {
  if (!Number.isFinite(windowMs) || windowMs <= 0) {
    return
  }

  setAuthConsistencyDeadlineMs(Date.now() + windowMs)
}

export const bootstrapAuthConsistencyWindow = (
  windowMs: number | null | undefined,
) => {
  if (
    windowMs === null ||
    windowMs === undefined ||
    !Number.isFinite(windowMs)
  ) {
    return
  }

  extendAuthConsistencyWindow(windowMs)
}

export const bootstrapAuthConsistencyWindowOnce = (
  bootstrapKey: string | null | undefined,
  windowMs: number | null | undefined,
) => {
  if (typeof window === 'undefined' || !bootstrapKey) {
    return
  }

  if (window.__AUTH_CONSISTENCY_LAST_BOOTSTRAP_KEY__ === bootstrapKey) {
    return
  }

  window.__AUTH_CONSISTENCY_LAST_BOOTSTRAP_KEY__ = bootstrapKey
  bootstrapAuthConsistencyWindow(windowMs)
}

export const consumeAuthConsistencyHeaderValue = (): string | null => {
  const deadlineMs = getAuthConsistencyDeadlineMs()
  if (deadlineMs === null || deadlineMs <= Date.now()) {
    clearAuthConsistencyAccountCreatedAt()
    return null
  }

  return AUTH_CONSISTENCY_FULLY_CONSISTENT_VALUE
}
